import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { customerApi, useCustomerAuth } from '../../lib/customerAuth';
import { useCart } from '../../lib/cart';
import { formatInr } from '../../lib/pricing';
import { loadRazorpay } from '../../lib/razorpay';
import CustomerHeader from '../../components/CustomerHeader';
import CustomerFooter from '../../components/CustomerFooter';
import LoginModal from '../../components/LoginModal';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { EmptyState } from '../../components/States';
import { ShoppingCart, CheckCircle2, Loader2, ReceiptIndianRupee, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { errMsg } from '../../lib/errors';

export default function Checkout() {
    const nav = useNavigate();
    const { items, subtotal, totalWeight, clear } = useCart();
    const { customer, isAuthed } = useCustomerAuth();
    const [loginOpen, setLoginOpen] = useState(false);
    const [address, setAddress] = useState({
        name: '', mobile: '', line1: '', line2: '', pincode: '',
        area: '', district: '', state: '', country: 'India', landmark: '',
    });
    const [pincodeOptions, setPincodeOptions] = useState([]);
    const [pincodeLoading, setPincodeLoading] = useState(false);
    const [quote, setQuote] = useState(null); // full quote incl. tax breakdown
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [placing, setPlacing] = useState(false);
    const [payMethod, setPayMethod] = useState('razorpay');
    const [rzpConfig, setRzpConfig] = useState(null); // { configured, mode, key_id }

    // Fetch Razorpay config (mode + key_id) once
    useEffect(() => {
        api.get('/payments/razorpay/config')
            .then(r => setRzpConfig(r.data))
            .catch(() => setRzpConfig({ configured: false, mode: 'test', key_id: '' }));
    }, []);

    // Prefill from customer profile
    useEffect(() => {
        if (customer) {
            setAddress(a => ({
                ...a,
                name: a.name || customer.name || '',
                mobile: a.mobile || customer.mobile || '',
                pincode: a.pincode || customer.location?.pincode || '',
                area: a.area || customer.location?.area || '',
                district: a.district || customer.location?.district || '',
                state: a.state || customer.location?.state || '',
                country: 'India',
            }));
        }
    }, [customer]);

    // Fetch full quote (subtotal + GST breakdown + shipping) when state or items change
    useEffect(() => {
        if (!address.state || items.length === 0) { setQuote(null); return; }
        setQuoteLoading(true);
        const level = customer?.level || 0;
        const customer_type = customer?.customer_type || 'retail';
        api.post('/orders/quote', {
            items: items.map(it => ({ product_id: it.product_id, qty: it.qty, unit_code: it.unit_code || null })),
            state: address.state,
            customer_type,
            level,
        })
            .then(r => setQuote(r.data))
            .catch(() => setQuote(null))
            .finally(() => setQuoteLoading(false));
    }, [address.state, items, customer]);

    const lookupPincode = async () => {
        if (!/^\d{6}$/.test(address.pincode)) { toast.error('Enter valid 6-digit pincode'); return; }
        setPincodeLoading(true);
        try {
            const r = await api.get(`/shipping/pincode/${address.pincode}`);
            if (r.data.success && r.data.post_offices.length > 0) {
                setPincodeOptions(r.data.post_offices);
                const p = r.data.post_offices[0];
                setAddress(a => ({ ...a, area: p.post_office, district: p.district, state: p.state, country: p.country }));
            } else { toast.error('Pincode not serviceable'); }
        } finally { setPincodeLoading(false); }
    };

    const selectArea = (name) => {
        const p = pincodeOptions.find(x => x.post_office === name);
        if (p) setAddress(a => ({ ...a, area: p.post_office, district: p.district, state: p.state }));
    };

    const placeOrder = async () => {
        if (!isAuthed) { setLoginOpen(true); return; }
        if (!address.name || !address.mobile || !address.line1 || !address.pincode || !address.state) {
            toast.error('Please fill all required address fields'); return;
        }
        if (!quote) { toast.error('Waiting for order quote. Please retry.'); return; }
        setPlacing(true);
        const capi = customerApi();
        try {
            // 1) Create Dharmik order (unpaid)
            const orderRes = await capi.post('/orders', {
                items: items.map(it => ({ product_id: it.product_id, qty: it.qty, unit_code: it.unit_code || null })),
                shipping_address: address,
                notes: `Payment method: ${payMethod}`,
            });
            const order = orderRes.data;

            // Cash on Delivery — no online payment
            if (payMethod === 'cod') {
                clear();
                nav(`/order-success/${order.order_number}?id=${order.id}`);
                return;
            }

            // 2) Razorpay flow (default)
            if (!rzpConfig?.configured) {
                toast.error(`Payment gateway not configured (${rzpConfig?.mode || 'test'} mode). Please contact support.`);
                return;
            }
            const rzpOrderRes = await capi.post('/payments/razorpay/create-order', {
                dharmik_order_id: order.id,
            });
            const rzp = rzpOrderRes.data;
            const Razorpay = await loadRazorpay();

            await new Promise((resolve) => {
                const options = {
                    key: rzp.key_id,
                    amount: rzp.amount,
                    currency: rzp.currency,
                    order_id: rzp.razorpay_order_id,
                    name: 'Dharmik Gardens',
                    description: `Order ${rzp.order_number || ''}`,
                    prefill: {
                        name: rzp.customer?.name || address.name || '',
                        email: rzp.customer?.email || '',
                        contact: rzp.customer?.contact || address.mobile || '',
                    },
                    notes: { order_number: rzp.order_number || '' },
                    theme: { color: '#16a34a' },
                    handler: async (response) => {
                        try {
                            const v = await capi.post('/payments/razorpay/verify', {
                                dharmik_order_id: order.id,
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                            });
                            if (v.data?.success) {
                                clear();
                                nav(`/order-success/${order.order_number}?id=${order.id}`);
                            } else {
                                toast.error('Payment verification failed');
                            }
                        } catch (e) {
                            toast.error(errMsg(e, 'Payment verification failed'));
                        } finally {
                            resolve();
                        }
                    },
                    modal: {
                        ondismiss: async () => {
                            try {
                                await capi.post('/payments/razorpay/failure', {
                                    dharmik_order_id: order.id,
                                    razorpay_order_id: rzp.razorpay_order_id,
                                    error: { reason: 'dismissed' },
                                });
                            } catch (_) { /* ignore */ }
                            toast.info('Payment cancelled. Your order is saved as unpaid.');
                            resolve();
                        },
                    },
                };
                const rp = new Razorpay(options);
                rp.on('payment.failed', async (response) => {
                    try {
                        await capi.post('/payments/razorpay/failure', {
                            dharmik_order_id: order.id,
                            razorpay_order_id: rzp.razorpay_order_id,
                            error: response.error || {},
                        });
                    } catch (_) { /* ignore */ }
                    toast.error(response?.error?.description || 'Payment failed');
                    resolve();
                });
                rp.open();
            });
        } catch (e) {
            toast.error(errMsg(e, 'Order failed'));
        } finally { setPlacing(false); }
    };

    const shipping = quote?.shipping ?? 0;
    const cgst = quote?.cgst ?? 0;
    const sgst = quote?.sgst ?? 0;
    const igst = quote?.igst ?? 0;
    const taxTotal = quote?.tax_total ?? 0;
    const grand = quote?.grand_total ?? (subtotal + shipping);
    const isIntra = quote?.is_intra_state;

    if (items.length === 0) {
        return (
            <div className="bg-white min-h-screen">
                <CustomerHeader />
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
                    <EmptyState icon={ShoppingCart} title="Cart is empty" description="Add items to your cart before checkout." action={<Button onClick={() => nav('/')} style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Shop now</Button>} />
                </div>
                <CustomerFooter />
            </div>
        );
    }

    return (
        <div className="bg-white min-h-screen">
            <CustomerHeader />
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">Checkout</h1>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-8 space-y-4">
                        {!isAuthed && (
                            <Card className="p-4 border-blue-200 bg-blue-50">
                                <div className="flex items-center justify-between">
                                    <div><div className="font-semibold">Sign in to continue</div><div className="text-sm text-neutral-600">Track orders, save addresses, and get wholesale prices.</div></div>
                                    <Button onClick={() => setLoginOpen(true)} data-testid="checkout-signin-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Sign In</Button>
                                </div>
                            </Card>
                        )}
                        <Card className="p-5">
                            <div className="text-sm font-semibold mb-3">Shipping Address</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div><Label>Full Name *</Label><Input value={address.name} onChange={e => setAddress({ ...address, name: e.target.value })} data-testid="chk-name" /></div>
                                <div><Label>Mobile *</Label><Input value={address.mobile} onChange={e => setAddress({ ...address, mobile: e.target.value })} data-testid="chk-mobile" /></div>
                                <div className="sm:col-span-2"><Label>Address Line 1 *</Label><Input value={address.line1} onChange={e => setAddress({ ...address, line1: e.target.value })} placeholder="House / Flat, Building, Street" data-testid="chk-line1" /></div>
                                <div className="sm:col-span-2"><Label>Address Line 2 (optional)</Label><Input value={address.line2} onChange={e => setAddress({ ...address, line2: e.target.value })} /></div>
                                <div>
                                    <Label>Pincode *</Label>
                                    <div className="flex gap-2">
                                        <Input value={address.pincode} onChange={e => setAddress({ ...address, pincode: e.target.value })} maxLength={6} data-testid="chk-pincode" />
                                        <Button variant="outline" onClick={lookupPincode} disabled={pincodeLoading} data-testid="chk-pincode-lookup">{pincodeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lookup'}</Button>
                                    </div>
                                </div>
                                <div>
                                    <Label>Area / Post Office</Label>
                                    {pincodeOptions.length > 0 ? (
                                        <select value={address.area} onChange={e => selectArea(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm" data-testid="chk-area-select">
                                            {pincodeOptions.map(p => <option key={p.post_office} value={p.post_office}>{p.post_office}</option>)}
                                        </select>
                                    ) : (
                                        <Input value={address.area} onChange={e => setAddress({ ...address, area: e.target.value })} placeholder="After lookup" />
                                    )}
                                </div>
                                <div><Label>District</Label><Input value={address.district} onChange={e => setAddress({ ...address, district: e.target.value })} /></div>
                                <div><Label>State *</Label><Input value={address.state} onChange={e => setAddress({ ...address, state: e.target.value })} data-testid="chk-state" /></div>
                                <div className="sm:col-span-2"><Label>Landmark (optional)</Label><Input value={address.landmark} onChange={e => setAddress({ ...address, landmark: e.target.value })} /></div>
                            </div>
                        </Card>

                        <Card className="p-5">
                            <div className="text-sm font-semibold mb-3">Payment Method</div>
                            <div className="space-y-2">
                                {[
                                    { v: 'razorpay', label: 'Card / UPI / Net Banking / Wallet (Razorpay)', badge: rzpConfig?.mode === 'live' ? 'LIVE' : 'TEST' },
                                    { v: 'cod', label: 'Cash on Delivery', badge: null },
                                ].map(o => (
                                    <label key={o.v} className={`flex items-center gap-2 p-3 rounded-md border cursor-pointer transition-colors ${payMethod === o.v ? 'border-green-500 bg-green-50' : 'hover:border-neutral-300'}`}>
                                        <input type="radio" name="pay" checked={payMethod === o.v} onChange={() => setPayMethod(o.v)} data-testid={`pay-${o.v}`} />
                                        <span className="text-sm flex-1">{o.label}</span>
                                        {o.badge && (
                                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${o.badge === 'LIVE' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                                {o.badge}
                                            </span>
                                        )}
                                    </label>
                                ))}
                            </div>
                            <div className="text-xs text-neutral-500 mt-2 flex items-center gap-1.5">
                                <ShieldCheck className="h-3.5 w-3.5 text-green-600" />
                                Payments are securely processed by Razorpay. We do not store your card details.
                                {rzpConfig && !rzpConfig.configured && (
                                    <span className="text-red-600 ml-2">Gateway not configured — contact support.</span>
                                )}
                            </div>
                        </Card>
                    </div>

                    <div className="lg:col-span-4">
                        <Card className="p-5 sticky top-24" data-testid="order-summary">
                            <div className="text-lg font-semibold mb-3">Order Summary</div>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {items.map(it => (
                                    <div key={it.product_id} className="flex items-center gap-2 text-sm">
                                        <div className="h-10 w-10 rounded bg-neutral-100 shrink-0 overflow-hidden">
                                            {it.image && <img src={it.image} alt="" className="h-full w-full object-cover" onError={(e) => e.currentTarget.style.display = 'none'} />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="truncate">{it.name}</div>
                                            <div className="text-xs text-neutral-500">Qty {it.qty} {it.unit_code || 'pcs'} × {formatInr(it.unit_price)}</div>
                                        </div>
                                        <div className="text-sm font-medium tabular-nums">{formatInr(it.unit_price * it.qty)}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 pt-4 border-t space-y-2 text-sm">
                                <div className="flex justify-between"><span className="text-neutral-600">Subtotal</span><span className="tabular-nums" data-testid="summary-subtotal">{formatInr(subtotal)}</span></div>

                                {/* GST Breakdown */}
                                {quoteLoading && !quote && (
                                    <div className="flex items-center gap-2 text-xs text-neutral-500"><Loader2 className="h-3 w-3 animate-spin" />Calculating GST…</div>
                                )}
                                {quote && taxTotal > 0 && (
                                    <div className="rounded-md bg-neutral-50 border p-2 space-y-1" data-testid="gst-breakdown">
                                        <div className="flex items-center gap-1 text-xs font-medium text-neutral-700">
                                            <ReceiptIndianRupee className="h-3.5 w-3.5" />
                                            GST ({isIntra ? 'Intra-state — CGST + SGST' : 'Inter-state — IGST'})
                                        </div>
                                        {isIntra ? (
                                            <>
                                                <div className="flex justify-between text-xs"><span className="text-neutral-600">CGST</span><span className="tabular-nums" data-testid="summary-cgst">{formatInr(cgst)}</span></div>
                                                <div className="flex justify-between text-xs"><span className="text-neutral-600">SGST</span><span className="tabular-nums" data-testid="summary-sgst">{formatInr(sgst)}</span></div>
                                            </>
                                        ) : (
                                            <div className="flex justify-between text-xs"><span className="text-neutral-600">IGST</span><span className="tabular-nums" data-testid="summary-igst">{formatInr(igst)}</span></div>
                                        )}
                                        <div className="flex justify-between text-xs font-medium pt-1 border-t border-neutral-200">
                                            <span>Total GST</span><span className="tabular-nums" data-testid="summary-tax-total">{formatInr(taxTotal)}</span>
                                        </div>
                                    </div>
                                )}
                                {quote && taxTotal === 0 && (
                                    <div className="text-xs text-neutral-500">No GST applicable on cart items.</div>
                                )}

                                <div className="flex justify-between">
                                    <span className="text-neutral-600">Shipping{address.state ? ` (${address.state})` : ''}</span>
                                    <span className="tabular-nums" data-testid="summary-shipping">{quote ? formatInr(shipping) : '—'}</span>
                                </div>
                                {quote && (
                                    <div className="text-xs text-neutral-500">Weight {quote.total_weight_kg} kg</div>
                                )}
                                <div className="flex justify-between text-base font-bold pt-2 border-t mt-2">
                                    <span>Grand Total</span>
                                    <span className="tabular-nums" data-testid="summary-grand-total">{formatInr(grand)}</span>
                                </div>
                                <div className="text-[11px] text-neutral-500 pt-1">All prices inclusive of applicable GST as per Indian tax law.</div>
                            </div>
                            <Button className="w-full mt-4" onClick={placeOrder} disabled={placing || (!!address.state && !quote)} data-testid="place-order-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                                {placing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Placing order...</> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Place Order &amp; Pay</>}
                            </Button>
                        </Card>
                    </div>
                </div>
            </div>
            <LoginModal open={loginOpen} onOpenChange={setLoginOpen} />
            <CustomerFooter />
        </div>
    );
}
